import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthContext';
import { supabase } from '@/lib/supabase';
import { hrProfileService } from '@/lib/hrProfileService';
import { useEmployeeUser, buildSessionFromRows } from './EmployeeUserContext';
import {
  Loader2, User, LogOut, Bell, Building2,
} from 'lucide-react';

export function EmployeeLayout() {
  const { user, loading: authLoading } = useAuth();
  const { session, seedSession, clearSession, setActiveEmployment } = useEmployeeUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [restoring, setRestoring] = useState(!session);

  useEffect(() => {
    if (authLoading || session) return;
    if (!user) {
      navigate('/employee', { replace: true });
      return;
    }
    (async () => {
      setRestoring(true);
      const { data: rows } = await hrProfileService.getEmployeeSession();
      if (rows.length > 0) {
        const s = buildSessionFromRows(rows);
        if (s) seedSession(s);
      } else {
        await supabase.auth.signOut();
        navigate('/employee', { replace: true });
      }
      setRestoring(false);
    })();
  }, [user, authLoading, session, navigate, seedSession]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    clearSession();
    navigate('/employee', { replace: true });
  };

  if (authLoading || restoring || !session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  const activeEmp = session.employments.find(e => e.id === session.activeEmploymentId);
  const pendingCount = session.employments.filter(e => e.status === 'pending_approval').length;
  const displayName = [session.profile.firstName, session.profile.lastName].filter(Boolean).join(' ') || 'עובד';

  const navItems = [
    { path: '/employee/portal', icon: User, label: 'פרופיל' },
    { path: '/employee/portal/employments', icon: Building2, label: 'מעסיקים' },
    ...(pendingCount > 0 ? [{
      path: '/employee/portal/approvals',
      icon: Bell,
      label: 'בקשות',
      badge: pendingCount,
    }] : []),
  ];

  return (
    <div dir="rtl" className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center">
              <span className="text-sm font-bold text-teal-700">
                {(session.profile.firstName?.[0] ?? '') + (session.profile.lastName?.[0] ?? '')}
              </span>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{displayName}</p>
              {activeEmp && (
                <p className="text-xs text-gray-500 truncate">{activeEmp.companyName}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Company switcher (if multiple employments) */}
            {session.employments.filter(e => e.status === 'active').length > 1 && (
              <select
                value={session.activeEmploymentId ?? ''}
                onChange={e => setActiveEmployment(e.target.value)}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white
                           text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-400"
              >
                {session.employments.filter(e => e.status === 'active').map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.companyName}</option>
                ))}
              </select>
            )}
            <button onClick={handleSignOut}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 max-w-lg mx-auto w-full px-4 py-6">
        <Outlet />
      </main>

      {/* Bottom navigation */}
      <nav className="sticky bottom-0 z-40 bg-white border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
        <div className="max-w-lg mx-auto px-4 flex">
          {navItems.map(item => {
            const isActive = location.pathname === item.path ||
              (item.path !== '/employee/portal' && location.pathname.startsWith(item.path));
            const Icon = item.icon;
            return (
              <button key={item.path}
                onClick={() => navigate(item.path)}
                className={[
                  'flex-1 flex flex-col items-center gap-0.5 py-3 transition-colors relative',
                  isActive ? 'text-teal-600' : 'text-gray-400 hover:text-gray-600',
                ].join(' ')}
              >
                <div className="relative">
                  <Icon className="w-5 h-5" />
                  {'badge' in item && item.badge ? (
                    <span className="absolute -top-1.5 -left-1.5 min-w-[18px] h-[18px] flex items-center
                                     justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1">
                      {item.badge}
                    </span>
                  ) : null}
                </div>
                <span className="text-[11px] font-medium">{item.label}</span>
                {isActive && (
                  <div className="absolute top-0 left-1/4 right-1/4 h-0.5 bg-teal-500 rounded-b" />
                )}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
